import { TrashIcon } from '@/components/common/icons';
import type { FC, ReactNode } from 'react';
import { useEffect, useId, useRef } from 'react';

/** What can take focus inside a dialog. */
const FOCUSABLE = 'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])';

/** Open modals, innermost last: Escape closes only the top one (a confirm over an editor). */
const openModals: object[] = [];

interface ModalProps {
  onClose: () => void;
  /** Width cap of the box, as in the prototype (430 export, 440 confirm). */
  maxWidth: number;
  /** Names the dialog for assistive technology. */
  labelledBy?: string;
  className?: string;
  /** Stacking layer; the confirmation sits above the editors, as in the prototype (90 over 70). */
  layer?: 'dialog' | 'confirm';
  children: ReactNode;
}

/**
 * The prototype's modal: a navy-tinted backdrop and a flat white box with a deep shadow.
 *
 * Escape and a click on the backdrop close it; focus moves into the box when it opens and back to
 * whatever had it when it closes.
 */
export const Modal: FC<ModalProps> = ({
  onClose,
  maxWidth,
  labelledBy,
  className = '',
  layer = 'dialog',
  children,
}) => {
  const boxRef = useRef<HTMLDivElement>(null);
  // Read through a ref, so a caller's inline `onClose` does not re-run the focus effect every render.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const token = {};
    openModals.push(token);
    const previous = document.activeElement as HTMLElement | null;
    // A field that took focus itself (`autoFocus`) keeps it; otherwise the first control gets it.
    if (!boxRef.current?.contains(document.activeElement)) {
      boxRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (openModals[openModals.length - 1] !== token) return;
      if (event.key === 'Escape') {
        closeRef.current();
        return;
      }
      // Focus trap: Tab and Shift+Tab cycle through the dialog's own controls.
      if (event.key !== 'Tab' || !boxRef.current) return;
      const focusable = [...boxRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (element) => !element.hasAttribute('disabled') && element.tabIndex !== -1
      );
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !boxRef.current.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !boxRef.current.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      openModals.splice(openModals.indexOf(token), 1);
      previous?.focus?.();
    };
  }, []);

  return (
    <div
      className={`fixed inset-0 ${layer === 'confirm' ? 'z-[90]' : 'z-[70]'} flex items-center justify-center bg-[rgba(10,35,60,.45)] p-6`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={boxRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={`w-full bg-white shadow-[0_20px_50px_rgba(10,35,60,.25)] ${className}`}
        style={{ maxWidth }}
      >
        {children}
      </div>
    </div>
  );
};

interface ConfirmModalProps {
  title: string;
  text: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  isBusy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** The prototype's destructive confirmation: red trash tile, title, text, "Zrušit" and a red action. */
export const ConfirmModal: FC<ConfirmModalProps> = ({
  title,
  text,
  confirmLabel,
  cancelLabel,
  isBusy = false,
  onConfirm,
  onCancel,
}) => {
  const titleId = useId();
  return (
    <Modal onClose={onCancel} maxWidth={440} labelledBy={titleId} className="flex flex-col" layer="confirm">
      <div className="flex gap-3 px-[22px] pb-4 pt-5">
        <span className="flex h-[34px] w-[34px] flex-none items-center justify-center bg-danger-soft text-danger">
          <TrashIcon size={16} />
        </span>
        <div className="flex min-w-0 flex-col gap-1.5">
          <div id={titleId} className="text-[15px] font-bold text-ink">
            {title}
          </div>
          <div className="text-[12.5px] leading-[1.6] text-muted">{text}</div>
        </div>
      </div>
      <div className="flex justify-end gap-2 border-t border-hairline px-[22px] pb-[18px] pt-3">
        <button
          type="button"
          onClick={onCancel}
          className="cursor-pointer rounded border border-control bg-white px-3.5 py-[9px] text-[12.5px] font-semibold text-muted hover:border-primary hover:text-primary"
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          disabled={isBusy}
          onClick={onConfirm}
          className="cursor-pointer rounded border-none bg-danger px-4 py-[9px] text-[12.5px] font-semibold text-white hover:bg-danger-deep disabled:cursor-progress disabled:opacity-60"
        >
          {confirmLabel}
        </button>
      </div>
    </Modal>
  );
};
